import * as ExcelJS from 'exceljs';
import { BalanceChange } from '../models/BalanceChange';
import { Interest } from '../models/Interest';
import { Payment } from '../models/Payment';
import { Repayment } from '../models/Repayment';

export class ExcelGeneratorService {
  /**
   * Generates an Excel file from balance changes data
   * @param balanceChanges List of balance changes to include in the report
   * @returns Promise resolving to an ExcelJS workbook
   */
  public async generateExcel(
    balanceChanges: BalanceChange[]
  ): Promise<ExcelJS.Workbook> {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Student Finance App';
    workbook.lastModifiedBy = 'Student Finance App';
    workbook.created = new Date();
    workbook.modified = new Date();

    // Add a worksheet
    workbook.addWorksheet('Paskolos suvestinė');
    const worksheet = workbook.addWorksheet('Paskolos išrašas');
    
    
    // Define columns
    worksheet.columns = [
      { header: 'Data', key: 'date', width: 12 },
      { header: 'Palūkanos', key: 'interestRate', width: 15 },
      { header: 'Pridėtos palūkanos', key: 'interestAmount', width: 15 },
      { header: 'Įmoka', key: 'repayment', width: 15 },
      { header: 'Gauta Paskola', key: 'payment', width: 15 },
      { header: 'Einamoji paskolos dalis', key: 'currentLoanPortion', width: 20 },
      { header: 'Einamoji palūkanų suma', key: 'currentInterestTotal', width: 20 },
      { header: 'Bendra paskola', key: 'totalLoan', width: 15 },
      { header: 'Paskolos %', key: 'loanPercentage', width: 12 },
      { header: 'Palūkanų %', key: 'interestPercentage', width: 12 },
      { header: 'Sumokėta paskolos (£)', key: 'paidLoanTotal', width: 18 },
      { header: 'Sumokėta palūkanų (£)', key: 'paidInterestTotal', width: 18 },
      { header: 'Sumokėta paskolos (€)', key: 'paidLoanTotalEur', width: 18 },
      { header: 'Sumokėta palūkanų (€)', key: 'paidInterestTotalEur', width: 18 },
      { header: 'LB kursas', key: 'ecbRate', width: 12 },
    ];
    
    // Sort by date
    const sortedChanges = [...balanceChanges].sort((a, b) => a.getDate().getTime() - b.getDate().getTime());
    
    // Filter out incomplete years (if the last year has only a few months)
    const filteredChanges = this.filterIncompleteYears(sortedChanges);

    // Add a summary worksheet
    this.createSummaryWorksheet(workbook, filteredChanges);
    
    // Add rows for each balance change
    for (const change of filteredChanges) {
      const row: any = {
        date: this.formatDate(change.getDate()),
        currentInterestTotal: this.formatCurrency(change.getTotalInterestUntil()),
        totalLoan: this.formatCurrency(change.getTotalLoanUntil() + change.getTotalInterestUntil()),
        currentLoanPortion: this.formatCurrency(change.getTotalLoanUntil()),
      };

      // Set values based on the type of balance change
      if (change instanceof Interest) {
        row.interestRate = `${(change.getInterestRate() * 100).toFixed(2)}%`;
        row.interestAmount = this.formatCurrency(change.getSum());
      } else if (change instanceof Payment) {
        row.payment = this.formatCurrency(change.getSum());
      } else if (change instanceof Repayment) {
        const repayment = change as Repayment;
        row.repayment = this.formatCurrency(repayment.getSum());
        
        // Calculate paid loan and interest portions for this repayment
        const loanPortion = repayment.getSum() * repayment.getPayedLoanPartPercent();
        const interestPortion = repayment.getSum() - loanPortion;
        
        row.paidLoanTotal = this.formatCurrency(loanPortion);
        row.paidInterestTotal = this.formatCurrency(interestPortion);
        
        // Add Euro values and ECB exchange rate
        if (repayment.getEcbExchangeRateForThatDate && typeof repayment.getEcbExchangeRateForThatDate === 'function') {
          const ecbRate = repayment.getEcbExchangeRateForThatDate();
          row.ecbRate = ecbRate ? ecbRate.toFixed(5) : 'N/A';
          
          if (repayment.getSumInEuro && typeof repayment.getSumInEuro === 'function') {
            const sumInEuro = repayment.getSumInEuro();
            if (sumInEuro) {
              const loanPortionEur = sumInEuro * repayment.getPayedLoanPartPercent();
              const interestPortionEur = sumInEuro - loanPortionEur;
              
              row.paidLoanTotalEur = this.formatEuroCurrency(loanPortionEur);
              row.paidInterestTotalEur = this.formatEuroCurrency(interestPortionEur);
            }
          }
        }
      }

      // Calculate percentages of current outstanding balance
      const totalOutstanding = change.getTotalLoanUntil() + change.getTotalInterestUntil();
      if (totalOutstanding > 0) {
        row.loanPercentage = `${((change.getTotalLoanUntil() / totalOutstanding) * 100).toFixed(2)}%`;
        row.interestPercentage = `${((change.getTotalInterestUntil() / totalOutstanding) * 100).toFixed(2)}%`;
      }

      const newRow = worksheet.addRow(row);
      
      // Apply green background for last year's entries
      const changeYear = change.getDate().getFullYear();
      // Get the latest year from all changes
      const latestYear = Math.max(...filteredChanges.map(c => c.getDate().getFullYear()));
      
      // Apply green background if entry is from the previous year
      if (changeYear === latestYear) {
        // Apply fill to cells A through N
        for (let col = 1; col <= 14; col++) { // Column N is 14th column
          const cell = newRow.getCell(col);
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ffd9ead3' } // Light green color
          };
        }
      }
    }

    // Apply styles
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    worksheet.getRow(1).height = 30; // Set header row height to 30 (default is ~15)
    
    
    // Freeze the header row and columns A through N
    worksheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'O2', activeCell: 'A2' }
    ];

    // Apply formatting to all cells (removed borders)
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        // Align numbers to right
        if (rowNumber > 1 && (
            typeof cell.value === 'number' || 
            (typeof cell.value === 'string' && 
             (cell.value.startsWith('£') || 
              cell.value.startsWith('€') || 
              cell.value.includes('%'))))) {
          cell.alignment = { horizontal: 'right' };
        }
        // Format percentage cells
        if (typeof cell.value === 'string' && cell.value.includes('%')) {
          // Keep percentage formatting
          cell.numFmt = '0.00%';
        }
        
        // Format currency cells
        if (typeof cell.value === 'string' && (cell.value.startsWith('£') || cell.value.startsWith('€'))) {
          // Keep currency formatting
          cell.numFmt = cell.value.startsWith('£') ? '£#,##0.00' : '€#,##0.00';
        }
      });
    });
    
    return workbook;
  }

  /**
   * Saves the workbook to a file
   * @param workbook ExcelJS workbook to save
   * @param filePath Path where to save the file
   */
  public async saveToFile(workbook: ExcelJS.Workbook, filePath: string): Promise<void> {
    await workbook.xlsx.writeFile(filePath);
  }

  /**
   * Generates a buffer containing the Excel file
   * @param workbook ExcelJS workbook to convert to buffer
   * @returns Promise resolving to an ArrayBuffer containing the Excel file
   */
  public async generateBuffer(workbook: ExcelJS.Workbook): Promise<ArrayBuffer> {
    return await workbook.xlsx.writeBuffer();
  }

  /**
   * Formats a date as YYYY-MM-DD
   * @param date Date to format
   * @returns Formatted date string
   */
  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  }

  /**
   * Formats a number as currency (£)
   * @param amount Amount to format
   * @returns Formatted currency string
   */
  private formatCurrency(amount: number): string {
    return `£${amount.toFixed(2)}`;
  }

  /**
   * Formats a number as currency (€)
   * @param amount Amount to format
   * @returns Formatted currency string
   */
  private formatEuroCurrency(amount: number): string {
    return `€${amount.toFixed(2)}`;
  }

  /**
   * Filters out incomplete years from the balance changes
   * If the last year has fewer than 10 months of data, it will be removed
   * @param changes Sorted balance changes
   * @returns Filtered balance changes
   */
  private filterIncompleteYears(changes: BalanceChange[]): BalanceChange[] {
    if (changes.length === 0) return changes;

    // Group changes by year
    const changesByYear = new Map<number, Set<number>>();
    
    for (const change of changes) {
      const date = change.getDate();
      const year = date.getFullYear();
      const month = date.getMonth();
      
      if (!changesByYear.has(year)) {
        changesByYear.set(year, new Set<number>());
      }
      
      changesByYear.get(year)?.add(month);
    }
    
    return changes;
  }

  /**
   * Creates a summary worksheet with yearly loan and interest payment information
   * @param workbook ExcelJS workbook to add the worksheet to
   * @param filteredChanges Filtered balance changes to calculate totals from
   */
  private createSummaryWorksheet(workbook: ExcelJS.Workbook, filteredChanges: BalanceChange[]): void {
    // Add a summary worksheet that resembles the image
    let summaryWorksheet = workbook.getWorksheet('Paskolos suvestinė');

    if (!summaryWorksheet) {
      summaryWorksheet = workbook.addWorksheet('Paskolos suvestinė');
    }
    
    // Set up the summary worksheet structure
    summaryWorksheet.columns = [
      { key: 'a', width: 15 },
      { key: 'b', width: 12 },
      { key: 'c', width: 25 },
      { key: 'd', width: 25 },
      { key: 'e', width: 15 },
      { key: 'f', width: 15 },
      { key: 'g', width: 15 },
      { key: 'h', width: 60 },
      { key: 'i', width: 15 },
    ];
    
    // Add title
    summaryWorksheet.mergeCells('B3:D4');
    summaryWorksheet.getCell('B3').value = 'Deklaracija';
    summaryWorksheet.getCell('B3').font = { bold: true, size: 17 };
    summaryWorksheet.getCell('B3').alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Add headers
    summaryWorksheet.getCell('B5').value = 'Metai';
    summaryWorksheet.getCell('B5').font = { bold: true };
    summaryWorksheet.getCell('B5').alignment = { horizontal: 'center' };
    summaryWorksheet.getCell('B5').border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
    
    summaryWorksheet.getCell('C5').value = 'Sumokėta paskolos (€)';
    summaryWorksheet.getCell('C5').font = { bold: true };
    summaryWorksheet.getCell('C5').alignment = { horizontal: 'center' };
    summaryWorksheet.getCell('C5').border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
    
    summaryWorksheet.getCell('D5').value = 'Sumokėta palūkanų (€)';
    summaryWorksheet.getCell('D5').font = { bold: true };
    summaryWorksheet.getCell('D5').alignment = { horizontal: 'center' };
    summaryWorksheet.getCell('D5').border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
    
    // Add data rows
    // Get unique years from filtered changes
    const years = Array.from(new Set(filteredChanges.map(change => change.getDate().getFullYear()))).sort();
    
    // Add a row for each year
    let rowIndex = 6;
    for (const year of years) {
      let loanPaidYear = 0;
      let interestPaidYear = 0;
      
      // Calculate totals for this year
      filteredChanges.forEach(change => {
        if (change instanceof Repayment && change.getDate().getFullYear() === year) {
          const repayment = change as Repayment;
          if (repayment.getSumInEuro && typeof repayment.getSumInEuro === 'function') {
            const sumInEuro = repayment.getSumInEuro();
            if (sumInEuro) {
              const loanPortionEur = sumInEuro * repayment.getPayedLoanPartPercent();
              const interestPortionEur = sumInEuro - loanPortionEur;
              
              loanPaidYear += loanPortionEur;
              interestPaidYear += interestPortionEur;
            }
          }
        }
      });
      
      summaryWorksheet.getCell(`B${rowIndex}`).value = year;
      summaryWorksheet.getCell(`C${rowIndex}`).value = `${loanPaidYear.toFixed(2)} €`;
      summaryWorksheet.getCell(`D${rowIndex}`).value = `${interestPaidYear.toFixed(2)} €`;
      
      rowIndex++;
    }
    
    // Add note section
    summaryWorksheet.getCell('G5').value = 'Pastaba:';
    summaryWorksheet.getCell('G5').font = { bold: true };

    summaryWorksheet.getCell('G20').value = 'Nuorodos:';
    summaryWorksheet.getCell('G20').font = { bold: true };


    // Links
    summaryWorksheet.getCell('H20').value = {
      text: 'Student Loan Company',
      hyperlink: 'https://www.gov.uk/government/organisations/student-loans-company',
    };

    summaryWorksheet.getCell('H21').value = {
      text: 'Paskolos išrašas',
      hyperlink: '#\'Paskolos išrašas\'!A1',
    };

    summaryWorksheet.getCell('H22').value = {
      text: 'LB kursas',
      hyperlink: 'https://www.lb.lt/lt/currency?class=Eu&type=day&currency=GBP',
    };
    
    summaryWorksheet.mergeCells('H5:H18');
    // Create a rich text value with hyperlink
    summaryWorksheet.getCell('H5').value = 'Kadangi paskolų suteikusi institucija (Student Loan Company) nepateikia įmokų išskaičiavimo, paskolos ir palūkanų dalys yra nurodytos Paskolos išrašo polapyje. Šis išrašas sudarytas remiantis oficialiais metiniais dokumentais, kuriuos pateikė paskolų suteikusi institucija. Minėti metinės ataskaitos dokumentai yra pridėti prie metinės pajamų deklaracijos.\n\n' +
      'Valiutų kursas apskaičiuotas pagal Europos Centrinio Banko (ECB) kasdien atnaujinamus ir skelbiamus valiutų santykius, kuriuos taip pat publikuoja ir Lietuvos Bankas (LB).\n\n' +
      'Įmokų išskaičiavimo skaičiavimai yra atliekami tiesioginės proporcijos principu pagal praėjusio mėnesio paskolos ir palūkanų procentalią proporciją.';
    
    summaryWorksheet.getCell('H5').alignment = { wrapText: true, vertical: 'top' };
    
    // Add "Deklaruojami metai" label
    summaryWorksheet.getCell('H2').value = '- Deklaruojami metai';
    summaryWorksheet.getCell('H2').font = { bold: true };

    // Set the declaration year in G2

    const latestYear = Math.max(...filteredChanges.map(c => c.getDate().getFullYear()));

    summaryWorksheet.getCell('G2').value = latestYear.toString();
    
    // Add green background to the latest year row
    [`B${rowIndex-1}`, `C${rowIndex-1}`, `D${rowIndex-1}`, 'G2'].forEach(cell => {
      if (summaryWorksheet && summaryWorksheet.getCell(cell)) {
        summaryWorksheet.getCell(cell).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ffd9ead3' } // Light green color
        };
      }
    });
  }
} 